// LLM generated - Model Claude 4 Sonnet
import { useEffect, useRef } from 'react';

/**
 * Check if the browser supports dvh (dynamic viewport height) units.
 * If it does, we don't need this hook at all - CSS handles it natively.
 */
function supportsDvh(): boolean {
  if (
    typeof window === 'undefined' ||
    typeof CSS === 'undefined' ||
    typeof CSS.supports !== 'function'
  ) {
    return false;
  }
  return CSS.supports('height', '100dvh');
}

/**
 * Hook that sets a CSS custom property (--visual-viewport-height) based on the
 * visual viewport height. This accounts for the virtual keyboard on mobile devices.
 *
 * IMPORTANT: This hook is only needed as a FALLBACK for browsers that don't support
 * the `dvh` CSS unit. Modern browsers (Chrome 108+, Safari 15.4+, Firefox 101+)
 * support `dvh` which handles this automatically without JavaScript.
 *
 * Performance notes:
 * - Skips entirely if browser supports `dvh` units
 * - Does NOT cause React re-renders - only mutates a CSS custom property
 * - Uses requestAnimationFrame to batch updates and reduce layout thrashing
 * - Event listeners are set up once on mount and cleaned up on unmount
 *
 * Usage:
 * 1. Call this hook once at a high level in your app (e.g., in a layout component)
 * 2. Use CSS with dvh as primary, fallback to the variable:
 *    ```css
 *    height: 100dvh;
 *    @supports not (height: 100dvh) {
 *      height: var(--visual-viewport-height, 100svh);
 *    }
 *    ```
 */
export function useVisualViewportHeight() {
  const handlerRef = useRef<(() => void) | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastHeightRef = useRef<number | null>(null);

  useEffect(() => {
    // Skip if browser supports dvh - CSS handles it natively
    if (supportsDvh()) {
      return;
    }

    // Check if visualViewport API is available (not available in SSR or older browsers)
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    // Create handler once and store in ref
    if (!handlerRef.current) {
      handlerRef.current = () => {
        // Cancel any pending update
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
        }

        // Use requestAnimationFrame to batch updates
        rafIdRef.current = requestAnimationFrame(() => {
          const height = window.visualViewport?.height || window.innerHeight;

          // Only update if height actually changed (avoids unnecessary style recalcs)
          if (height !== lastHeightRef.current) {
            lastHeightRef.current = height;
            document.documentElement.style.setProperty('--visual-viewport-height', `${height}px`);
          }

          rafIdRef.current = null;
        });
      };
    }

    const handler = handlerRef.current;

    // Set initial value
    handler();

    // Listen for viewport changes (keyboard open/close, zoom, etc.)
    window.visualViewport.addEventListener('resize', handler);

    // Also listen for scroll events on visual viewport (iOS sometimes needs this)
    window.visualViewport.addEventListener('scroll', handler);

    return () => {
      // Clean up pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      window.visualViewport?.removeEventListener('resize', handler);
      window.visualViewport?.removeEventListener('scroll', handler);
    };
  }, []);
}

export default useVisualViewportHeight;
